<script lang="ts" setup>
import { UploadTaskSnapshot } from "firebase/storage";
import { GoogleDriveResource } from "~/classes/models/education-resources/education-resource.model";

const props = defineProps({
  isGoogleImportEnabled: {
    type: Boolean,
    default: true,
  },
  convertGoogleFile: {
    type: Boolean,
    default: true,
  },
  allowMultiple: {
    type: Boolean,
    default: false,
  },
  path: {
    type: String,
  },
  mode: {
    type: String as PropType<"button" | "row">,
    default: "button",
  },
  returnRawFiles: {
    type: Boolean,
    default: false,
  },
  acceptedMimeTypes: {
    type: Array as PropType<string[]>,
    default: () => [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf",
      "text/plain",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.apple.pages",
      "application/vnd.apple.keynote",
      "image/*",
    ],
  },
});

const emit = defineEmits<{
  (e: "onGoogleFilePickStart"): void;
  (e: "onGoogleFilePickEnd"): void;
  (e: "onFileUploaded", mediaObject: MediaObject): void;
  (e: "onFilesUploaded", mediaObjects: MediaObject[]): void;
  (e: "onRawFileUploaded", file: File | CustomFile): void;
  (e: "onRawFilesUploaded", files: File[] | CustomFile[]): void;
  (e: "onGoogleResourceUploaded", resource: GoogleDriveResource): void;
}>();

const appUserDataStore = useAppUserDataStore();
const { isPublicDemo } = storeToRefs(appUserDataStore);

const fileRef = ref<HTMLInputElement | null>(null);
const onChooseFile = () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t choose a file to upload in demo mode.");
    return;
  }

  fileRef.value?.click();
};

const isUploading = ref(false);
const uploadProgress = ref(0);

const onPickGoogle = () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t choose a file to upload in demo mode.");
    return;
  }

  emit("onGoogleFilePickStart");

  try {
    let mimeTypeMap = {
      "application/msword": "documents",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        "documents",
      "application/pdf": "pdf",
      "text/plain": "documents",
      "application/vnd.ms-powerpoint": "slides",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        "slides",
      "application/vnd.apple.pages": "documents",
      "application/vnd.apple.keynote": "slides",
      "image/*": "images",
    } as any;

    let supportedMimeTypes = [
      "documents",
      "pdf",
      "images",
      "drawings",
      "sheets",
      "slides",
    ] as any;

    if (props.acceptedMimeTypes.length != 9) {
      supportedMimeTypes = props.acceptedMimeTypes.map((mimeType) => {
        return mimeTypeMap[mimeType as any];
      });
    }

    usePickGoogleDriveFile({
      supportedMimeTypes: supportedMimeTypes,
      returnFile: props.convertGoogleFile,
      onFilePicked: props.allowMultiple ? undefined : onPickedGoogleFile,
      onFilesPicked: props.allowMultiple ? onPickedGoogleFile : undefined,
      allowMultiple: props.allowMultiple,
    }).pickGoogleDriveFile();
  } catch (error) {
    emit("onGoogleFilePickEnd");
  }
};

const isUploadingGoogle = ref(false);

const onPickedGoogleFile = async (
  files: CustomFile | GoogleDriveResource | CustomFile[]
) => {
  if (files == undefined || (Array.isArray(files) && files.length == 0)) {
    emit("onGoogleFilePickEnd");
    return;
  }

  if (props.returnRawFiles == true) {
    if (Array.isArray(files)) {
      emit("onRawFilesUploaded", files);
    } else {
      emit("onRawFileUploaded", files as CustomFile);
    }
    emit("onGoogleFilePickEnd");
    return;
  }

  isUploadingGoogle.value = true;

  if (props.convertGoogleFile != true) {
    isUploadingGoogle.value = false;
    emit("onGoogleResourceUploaded", files as GoogleDriveResource);
    emit("onGoogleFilePickEnd");
    return;
  }

  try {
    for (const file of (Array.isArray(files) ? files : [files]) as CustomFile[]) {
      const mediaObject = await createMediaObject(file, props.path);
      await uploadFileToFirebase(file.data, mediaObject);
    }
  } catch (error) {
    console.error("Google file upload failed", error);
    useBaseAlert("Upload failed", "Please try choosing your files again.");
  } finally {
    isUploadingGoogle.value = false;
    emit("onGoogleFilePickEnd");
  }
};

const onFileSelected = async (ev: Event) => {
  const input = ev.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = "";
  if (!files.length || isUploading.value) return;

  if (props.returnRawFiles == true) {
    emit("onRawFilesUploaded", files);
    return;
  }

  isUploading.value = true;
  try {
    for (const file of files) {
      const mediaObject = await createMediaObject(file, props.path);
      await uploadFileToFirebase(file, mediaObject);
    }
  } catch (error) {
    console.error("File upload failed", error);
    useBaseAlert("Upload failed", "Please check your connection and try again.");
  } finally {
    isUploading.value = false;
  }
};

const { createMediaObject, uploadFile, getDownloadURL } = useFileUploader();

const uploadFileToFirebase = async (
  file: File | Uint8Array | Blob | ArrayBuffer,
  mediaObject: MediaObject
) => {
  const uploadTask = uploadFile(file, mediaObject);

  uploadTask.on("state_changed", (snapshot: UploadTaskSnapshot) => {
    uploadProgress.value = snapshot.bytesTransferred / snapshot.totalBytes;
  });

  const snapshot = await uploadTask;
  mediaObject.mediaHref = await getDownloadURL(snapshot.ref);
  uploadProgress.value = snapshot.bytesTransferred / snapshot.totalBytes;
  emit("onFileUploaded", mediaObject);
};

const uploadProgressLabel = computed(() => {
  return `Uploading...${Math.round(uploadProgress.value * 100)}%`;
});
</script>

<template>
  <div class="file-upload-button">
    <div v-if="mode == 'row'" class="flex flex-row gap-2 items-center">
      <BaseTextButton @click="onChooseFile" :show-spinner="isUploading">
        <div class="flex flex-row items-center w-full">
          <div
            class="rounded-full border-link border-2 p-1 aspect-square flex items-center justify-center"
          >
            <icon name="lucide:file-up" size="20" />
          </div>
          <span class="ml-2"> Computer </span>
          <input
            ref="fileRef"
            class="hidden"
            type="file"
            :accept="acceptedMimeTypes.join(',')"
            :multiple="allowMultiple"
            @change="onFileSelected"
          />
        </div>
      </BaseTextButton>
      <span>|</span>
      <BaseTextButton class="" @click="onPickGoogle">
        <div class="flex flex-row items-center w-full">
          <!-- <icon name="logos:google-drive" size="20" /> -->
          <div
            class="rounded-full border-link border-2 p-1 aspect-square flex items-center justify-center"
          >
            <icon name="mdi:google-drive" size="20" />
          </div>

          <span class="ml-2"> Google Drive </span>
        </div>
      </BaseTextButton>
    </div>
    <BasePopover
      v-if="mode == 'button'"
      :disabled="isGoogleImportEnabled != true"
      container=".file-upload-button"
    >
      <span>
        <slot
          name="trigger"
          :is-uploading="isUploading"
          :upload-progress="uploadProgress"
        >
          <BaseTextButton
            :show-spinner="isUploading"
            class="border border-primary-default my-2 h-[45px]"
          >
            <icon name="material-symbols:upload" size="20" />
            <span class="ml-1">
              <slot v-if="isUploading != true"> Choose File </slot>
              <span v-if="isUploading">
                {{ uploadProgressLabel }}
              </span>
            </span>
          </BaseTextButton>
        </slot>
        <input
          ref="fileRef"
          class="hidden"
          type="file"
          :accept="acceptedMimeTypes.join(',')"
          :multiple="allowMultiple"
          @change="onFileSelected"
        />
      </span>
      <template #content>
        <div class="flex flex-col bg-surface p-1">
          <BaseTextButton v-close-popper class="w-full" @click="onChooseFile">
            <div class="flex flex-row items-center w-full">
              <icon name="material-symbols:upload" size="20" />
              <span class="ml-4"> From Computer </span>
            </div>
          </BaseTextButton>
          <BaseTextButton
            v-close-popper
            class="w-full"
            @click="onPickGoogle"
            :show-spinner="isUploadingGoogle"
          >
            <div class="flex flex-row items-center w-full">
              <icon name="mdi:google-drive" size="20" />

              <span class="ml-4"> From Google Drive </span>
            </div>
          </BaseTextButton>
        </div>
      </template>
    </BasePopover>
  </div>
</template>

<style scoped></style>
